"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Menu, X } from "lucide-react"
import { useMobileMenu } from "./hooks/use-mobile-menu"

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isMobile = useMobileMenu()

  // Close menu when switching from mobile to desktop
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }, [isMobile, isMenuOpen])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest(".mobile-menu") && !event.target.closest(".menu-button")) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isMenuOpen])

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [isMenuOpen])

  const toggleMenu = (e) => {
    e.stopPropagation() // Prevent the event from bubbling up
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <>
      <header style={styles.header}>
        <Link to="/">
          <img src="/hasbara_logo.png" alt="Hasbara Logo" style={styles.logo} />
        </Link>

        {/* Desktop Navigation */}
        {!isMobile && (
          <nav style={styles.nav}>
            <ul style={styles.navList}>
              <li>
                <Link to="/" style={styles.navItem}>
                  Home
                </Link>
              </li>
              {/* <li>
                <Link to="/stats" style={styles.navItem}>
                  Stats
                </Link>
              </li> */}
              <li>
                <a href="/#get-started" style={styles.navItem}>
                  Try It
                </a>
              </li>
              <li>
                <a href="/#vision" style={styles.navItem}>
                  Vision
                </a>
              </li>
            </ul>
          </nav>
        )}

        {/* Mobile Burger Menu Button */}
        {isMobile && (
          <button
            className="menu-button"
            onClick={(e) => toggleMenu(e)}
            style={styles.menuButton}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
      </header>

      {/* Mobile Navigation Menu - Moved outside header */}
      {isMobile && (
        <>
          {/* Overlay */}
          {isMenuOpen && <div style={styles.overlay} onClick={() => setIsMenuOpen(false)} />}

          {/* Menu */}
          <div
            className="mobile-menu"
            style={{
              ...styles.mobileNav,
              transform: isMenuOpen ? "translateX(0)" : "translateX(100%)",
            }}
          >
            {/* Close button (X) in the top right corner */}
            <button onClick={() => setIsMenuOpen(false)} style={styles.closeButton} aria-label="Close menu">
              <X size={24} />
            </button>

            <ul style={styles.mobileNavList}>
              <li>
                <Link to="/" style={styles.mobileNavItem} onClick={() => setIsMenuOpen(false)}>
                  Home
                </Link>
              </li>
              {/* <li>
                <Link to="/stats" style={styles.mobileNavItem} onClick={() => setIsMenuOpen(false)}>
                  Stats
                </Link>
              </li> */}
              <li>
                <a href="/#get-started" style={styles.mobileNavItem} onClick={() => setIsMenuOpen(false)}>
                  Try It
                </a>
              </li>
              <li>
                <a href="/#vision" style={styles.mobileNavItem} onClick={() => setIsMenuOpen(false)}>
                  Vision
                </a>
              </li>
            </ul>
          </div>
        </>
      )}
    </>
  )
}

const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 28px",
    backgroundColor: "#004aad",
    color: "white",
    boxShadow: "0 6px 24px rgba(0, 0, 0, 0.14)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
  },
  logo: {
    height: "48px",
    maxHeight: "48px",
    display: "block",
  },
  nav: {
    flex: 1,
    display: "flex",
    justifyContent: "right",
  },
  navList: {
    listStyle: "none",
    display: "flex",
    gap: "36px",
    margin: 0,
    padding: 0,
    alignItems: "center",
  },
  navItem: {
    color: "rgba(255, 255, 255, 0.92)",
    textDecoration: "none",
    fontSize: "16px",
    fontWeight: "500",
    transition: "color 0.2s ease",
  },
  menuButton: {
    background: "none",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1002,
  },
  closeButton: {
    position: "absolute",
    top: "20px",
    right: "20px",
    background: "none",
    border: "none",
    color: "white",
    cursor: "pointer",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1003,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    transition: "background-color 0.2s ease",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1001,
  },
  mobileNav: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: "50%",
    backgroundColor: "#004aad",
    zIndex: 1002,
    transition: "transform 0.3s ease-in-out",
    boxShadow: "-5px 0 15px rgba(0, 0, 0, 0.1)",
    overflowY: "auto",
    paddingTop: "50px",
  },
  mobileNavList: {
    listStyle: "none",
    padding: "20px",
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  mobileNavItem: {
    color: "white",
    textDecoration: "none",
    fontSize: "20px",
    fontWeight: "500",
    display: "block",
    padding: "10px 0",
  },
}

export default Header
